import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { AlertTriangle, Loader2 } from 'lucide-react';

const PublicPageViewer = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Handle redirects for functional pages
  useEffect(() => {
    const functional = ['docs', 'download', 'contact', 'changelog', 'blog'];

    if (slug && functional.includes(slug)) {
      navigate('/' + slug, { replace: true });
    }
  }, [slug, navigate]);

  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['publicPage', slug],
    queryFn: async () => {
      const res = await axiosClient.get('/public/pages/' + slug);
      return res.data || res;
    },
    retry: 1,
    enabled:
      !!slug &&
      !['docs', 'download', 'contact', 'changelog', 'blog'].includes(slug),
  });

  if (isLoading)
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mr-3" />
        Loading...
      </div>
    );

  if (isError || !page)
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-12 text-center">
        <AlertTriangle className="w-16 h-16 text-red-500/50 mb-6" />

        <h2 className="text-3xl font-black text-white">404</h2>

        <p className="text-slate-500 mt-2">
          The dynamic page "{slug}" was not found.
        </p>

        <div className="mt-8 p-4 bg-blue-500/5 border border-blue-500/10 rounded-lg text-xs text-teal-400 max-w-sm">
          <strong>Admin Note:</strong> Ensure slug "{slug}" exists in the DB. If
          this is a module, use the path {'/' + slug} instead.
        </div>

        <button
          onClick={() => navigate('/')}
          className="mt-8 px-8 py-2 bg-slate-800 text-white rounded-lg"
        >
          Back to Home
        </button>
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto py-24 px-6 animate-in fade-in duration-700">
      <h1 className="text-5xl font-black text-white mb-10 tracking-tight">
        {page.title}
      </h1>

      <div
        className="
          prose prose-invert prose-lg max-w-none
          prose-headings:text-white
          prose-a:text-teal-400
          prose-strong:text-white
        "
        dangerouslySetInnerHTML={{
          __html: page.content || page.content_html,
        }}
      />
    </div>
  );
};

export default PublicPageViewer;
